import TuneRounded from "@mui/icons-material/TuneRounded";
import isArray from "lodash/isArray";
import isNil from "lodash/isNil";
import size from "lodash/size";
import { useCallback, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import RffSelectMultipleUserEmailField from "../../components/finalForm/inputs/RffSelectMultipleUserEmailField";
import RffTextField from "../../components/finalForm/inputs/RffTextField";
import RffForm from "../../components/finalForm/RffForm";
import RffGroup from "../../components/finalForm/shared/RffGroup";
import Flx from "../../components/layout/Flx";
import Htag from "../../components/typography/Htag";
import TitledCard from "../../components/ui/TitledCard/TitledCard";
import PrefillLoanDataSection from "./PrefillLoanDataSection";
import { useEnsureUsers } from "../../hooks/useEnsureReferenceData";
import { useUnderwritingHook } from "../../hooks/useUnderwritingHook";
import { useIsAcquisitions, usePipeline } from "../../pipelines/PipelineContext";
import { loanPipelineClear } from "../../redux/actions/loanPipelineActions";
import BorrowersSection from "./BorrowersSection";
import BorrowingEntitiesSection from "./BorrowingEntitiesSection";
import FileUploadSection from "./FileUploadSection";
import LoanDetailSection from "./LoanDetailSection";
import PropertiesSection from "./PropertiesSection";
import Screen from "../../components/layout/Screen";
import { addressLabel } from "../../_src_shared/utils/functions/addressLabel";
import {
  DELAYED_PURCHASE_FIELD,
  canBeDelayedPurchase,
  normalizeLegacyDelayedPurchase,
} from "../../utils/loans/delayedPurchase";

const LoanQuickIntakeFormScreen = () => {
  // Populate the `users` Redux slice so the "Permitted Users On Loan" picker
  // (RffSelectMultipleUserEmailField) has options. Users are no longer fetched
  // eagerly in AuthenticatedDashboard — each screen that needs them ensures
  // them lazily (cache-gated, de-duped).
  useEnsureUsers();

  const defaultValues = useMemo(() => {
    return {
      borrowers: [{}],
      subjectProperties: [{}],
      pipeline: "Underwriting",
    };
  }, []);

  const [success, setSuccess] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [formData, setFormData] = useState(null);
  // What the loan-application PDF prefill returned — drives the "what the
  // import found" panel only. Submitting goes through the normal create path
  // either way: the PDF rides along in the form's uploadMetadata.
  const [importedData, setImportedData] = useState();

  const { loading, createLoan, createLoanWithFileUploads } =
    useUnderwritingHook();

  // Pipeline-aware: under <PipelineProvider pipeline="acquisitions"> createLoan
  // already posts to /loan_acquisitions/create_loan, so the only pipeline-specific
  // bit left is WHERE we land after — the acquisitions drilldown vs the preclose one.
  const { drilldownBase, pipelinePath } = usePipeline();
  const isAcquisitions = useIsAcquisitions();
  const screenTitle = isAcquisitions ? "Create Acquisition Loan" : "Create Loan";

  const handleCreateLoanSuccess = useCallback(
    (loanId) => {
      resetFormData();

      setSuccess(true);
      dispatch(loanPipelineClear());

      // Land on the drilldown for THIS pipeline (preclose /loan/{id} or
      // acquisitions /acquisition/{id}); fall back to that pipeline's list.
      if (loanId) {
        navigate(`${drilldownBase}/${loanId}`);
      } else {
        navigate(pipelinePath);
      }
    },
    [dispatch, navigate, drilldownBase, pipelinePath],
  );

  const onFormSubmit = (formData) => {
    let data = { ...normalizeLegacyDelayedPurchase(formData) };
    // delayedPurchase only applies to a cash-out refinance (the backend clears
    // it otherwise) — never post a leftover answer for another purpose.
    if (!canBeDelayedPurchase(data.loanPurpose)) {
      delete data[DELAYED_PURCHASE_FIELD];
    }

    // Auto-generate loanName from first property address if not set
    let loanName = formData?.loanName;
    if (isNil(loanName) && isArray(data?.subjectProperties)) {
      const propertyName = addressLabel(data?.subjectProperties[0]?.address);
      if (propertyName) {
        loanName = propertyName;
      }
    }
    data.loanName = loanName;

    // A PDF-prefilled form carries the application in uploadMetadata, so it
    // takes the file-upload route like any other loan with attachments. (The
    // old import posted to create_loan_from_ocr_app, which ignored the PDF and
    // always created an underwriting loan — even from acquisitions.)
    if (isArray(data?.uploadMetadata) && size(data?.uploadMetadata) >= 1) {
      createLoanWithFileUploads({
        data,
        onSuccessFn: (d) => handleCreateLoanSuccess(d?._id),
      });
    } else {
      createLoan({
        data,
        onSuccessFn: (d) => handleCreateLoanSuccess(d?._id),
      });
    }
  };

  const resetFormData = useCallback(() => {
    setFormData(defaultValues);

    setImportedData(null);
  }, [defaultValues]);

  const initialState = useMemo(() => {
    if (isNil(formData)) {
      return defaultValues;
    }
    // A sizer / application import can still carry the retired "Delayed
    // Purchase" purpose — seed it as cash-out + the flag so the form can show it.
    return normalizeLegacyDelayedPurchase(formData);
  }, [formData, defaultValues]);

  return (
    <Screen
      sx={{
        pb: 6,
        background: "#f5f8fa",
        // height: "100%",
        overflowY: "auto",
      }}
    >
      {/* <Box
        sx={{
          background: "#ffffff",
          pt: 3,
          pb: 3,
          boxShadow: "0 0 0px 1px #dde0e4",
        }}
      >
        <ScreenContent suppressPaddingTopBottom>
          <Flx column gap={3}>
            <Flx fw jb ac>
              <Flx ac gap={1}>
                <NoteAddOutlined className="thin" />
                <Htag h1>Create Loan</Htag>
              </Flx>
            </Flx>
          </Flx>
        </ScreenContent>
      </Box> */}
      <RffForm
        className={"required"}
        loading={loading}
        success={success}
        // formSpy
        submitText="Create Loan"
        maxWidth={"md"}
        sx={{
          minHeight: "unset",
          // pb: 6,
          // background: "#f5f8fa",
          // height: "100%",
          // overflowY: "auto",
          // ".titled-group-body-root": {
          //   p: 2.5,
          // },
          // ".titled-group-header-root": {
          //   "h1, svg": {
          //     color: "var(--titleColor)",
          //   },
          // },
          // h4: {
          //   fontWeight: 500,
          //   fontSize: "17px !important",
          // },
        }}
        initialValues={initialState}
        onSubmit={onFormSubmit}
        formTitle={screenTitle}
        formDescription="Please provide as much information as possible regarding your loan, borrowers, and properties."
        finalStepLabel="Submit"
      >
        <Flx
          column
          flexGrow={0}
          gap={2}
          sx={{
            py: 4,
            // h1: {
            //   fontSize: "17px",
            // },

            // ".titled-group-header-title-content .MuiSvgIcon-root": {
            //   fontSize: "18px !important",
            // },
          }}
        >
          <Flx column gap={2}>
            <Htag h2>{screenTitle}</Htag>
            {/* Both import paths — loan application PDF and EV product sizer —
                live in one section: same shape of task, same affordance. Each
                stages values into the form for review; neither saves. */}
            <PrefillLoanDataSection
              setFormData={setFormData}
              setImportedData={setImportedData}
              importedData={importedData}
            />
          </Flx>

          <CreateLoanFormQuestions />
        </Flx>
      </RffForm>
    </Screen>
  );
};

// ════════════════════════════════════════════════════════════════
// Sub-components (unchanged)
// ════════════════════════════════════════════════════════════════

export const CreateLoanFormQuestions = ({
  sx,
  gap = 2,
  cardHeaderFontSize = "17px",
  cardHeaderIconFontSize = "18px",
  defaultExpanded = true,
  mobile = false,
  hideConfigSection = false,
  hideFileUploadSection = false,
}) => {
  return (
    <Flx
      column
      flexGrow={0}
      gap={gap}
      sx={{
        h1: {
          fontSize: cardHeaderFontSize,
        },

        ".titled-group-header-title-content .MuiSvgIcon-root": {
          fontSize: `${cardHeaderIconFontSize} !important`,
        },

        ".titled-group-body-root": {
          p: 2.5,
        },
        ".titled-group-header-root": {
          "h1, svg": {
            color: "text.title",
          },
        },
        h4: {
          fontWeight: 500,
          fontSize: "17px !important",
        },
        ...sx,
      }}
    >
      <LoanDetailSection defaultExpanded={defaultExpanded} mobile={mobile} />
      <BorrowersSection defaultExpanded={defaultExpanded} mobile={mobile} />
      <PropertiesSection defaultExpanded={defaultExpanded} mobile={mobile} />
      <BorrowingEntitiesSection
        defaultExpanded={defaultExpanded}
        mobile={mobile}
      />
      {hideFileUploadSection ? null : (
        <FileUploadSection defaultExpanded={defaultExpanded} mobile={mobile} />
      )}
      {hideConfigSection ? null : (
        <TitledCard
          collapsible
          defaultExpanded={defaultExpanded}
          icon={<TuneRounded className="thin" />}
          variant="h1"
          title="Loan Configuration"
        >
          <RffGroup suppressBottomMargin>
            <RffTextField
              name="loanName"
              label="Loan Name"
              helperText="Display name that will be visible in the loan pipeline table"
            />
            {/* <RffSelectField
              name="pipeline"
              label="Select Pipeline"
              size={6}
              required
              options={selectOptionsPipelineType}
            /> */}
            {/* <RffSelectUserEmailField
              size={6}
              name="salesperson"
              required
              label="Assigned Salesperson"
            /> */}
            <RffSelectMultipleUserEmailField
              name="permittedUsers"
              label="Permitted Users On Loan"
            />
          </RffGroup>
        </TitledCard>
      )}
    </Flx>
  );
};

// const DownloadLoanUploadTemplate = () => {
//   const { loading, downloadLoanUploadTemplate } = useLoanFilesHook();

//   const onClick = () => {
//     downloadLoanUploadTemplate({});
//   };
//   return (
//     <Button
//       loading={loading}
//       fullWidth={false}
//       variant="text"
//       onClick={onClick}
//       startIcon={<DownloadOutlined />}
//     >
//       Download Excel Template
//     </Button>
//   );
// };

// const UploadExcelContent = ({ setFormData }) => {
//   const { loading, populateLoanFormFromExcel } = useUnderwritingHook();

//   const onUpload = (excel) => {
//     populateLoanFormFromExcel({
//       excel: excel[0],
//       onSuccessFn: (data) => {
//         setFormData(data);
//       },
//     });
//   };
//   return (
//     <>
//       <Box sx={{ pt: 2 }}>
//         <Flx fw jb ac>
//           <Txt>Quick Intake Excel Template Upload</Txt>
//           <DownloadLoanUploadTemplate />
//         </Flx>
//         <FileUploadField
//           suppressGrid
//           sx={{ width: "100%" }}
//           onFilesAdded={onUpload}
//           suppressFileList
//           loading={loading}
//           borderSize={3}
//           minHeight="100px"
//           suppressUploadButton
//           uploadText={"Upload Quick Intake Loan Template Excel"}
//         />
//       </Box>
//     </>
//   );
// };

// const TemplatesRenderer = ({ saves, onUpdate }) => {
//   if (isEmpty(saves)) {
//     return null;
//   }

//   return (
//     <Flx ac gap={1}>
//       <Txt sx={{ flexShrink: 0 }}>Saved Templates:</Txt>
//       <LocalStorageOptionRenderer
//         saves={saves}
//         lsPrefix={lsPrefix}
//         onUpdate={onUpdate}
//       />
//     </Flx>
//   );
// };

export default LoanQuickIntakeFormScreen;
